import { useState } from 'react';
import { EggSvg } from './EggSvg';
import { RarityPill } from './RarityPill';
import { ProbabilityBars } from './ProbabilityBars';
import { CoinIcon } from '@/components/coins/CoinIcon';
import { dominantRarity, formatHatchTime } from '@/constants/rewards';
import type { EggTierView, EggTierId } from '@/types/rewards';

interface EggShopCardProps {
  tier: EggTierView;
  coinBalance: number;
  nestFull: boolean;
  onBuy: (tierId: EggTierId) => void;
  purchasing: boolean;
}

export function EggShopCard({ tier, coinBalance, nestFull, onBuy, purchasing }: EggShopCardProps) {
  const [shaking, setShaking] = useState(false);
  const canAfford = coinBalance >= tier.cost;
  const canBuy = canAfford && !nestFull && !purchasing;

  const handleClick = () => {
    if (nestFull) return;
    if (!canAfford) {
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
      return;
    }
    onBuy(tier.id);
  };

  return (
    <div
      className={`flex flex-col rounded-2xl border bg-white overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-md ${
        shaking ? 'animate-shake-x' : ''
      } ${canBuy ? 'border-stone-200 hover:border-stone-300' : 'border-stone-200'}`}
    >
      {/* Egg display */}
      <div
        className="flex items-center justify-center pt-6 pb-4"
        style={{ background: 'linear-gradient(180deg, #FAF6EE, #FFF)' }}
      >
        <div className="animate-egg-bob-slow">
          <EggSvg tierId={tier.id} size={80} />
        </div>
      </div>

      <div className="flex flex-col flex-1 px-4 pb-4">
        <h3 className="text-lg font-display font-bold text-stone-800 mb-1">{tier.name}</h3>
        <RarityPill rarity={dominantRarity(tier.pool)} className="self-start mb-2" />
        <p className="text-xs text-stone-400 mb-3 leading-relaxed">{tier.blurb}</p>

        <div className="mb-4">
          <ProbabilityBars pool={tier.pool} />
        </div>

        {/* Spacer pushes the banner + footer to the bottom so cards align */}
        <div className="mt-auto" />

        {!canAfford && !nestFull && (
          <p className="text-xs font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 mb-3 text-center">
            Need {(tier.cost - coinBalance).toLocaleString()} more coins
          </p>
        )}
        {nestFull && (
          <p className="text-xs font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 mb-3 text-center">
            Nest is full
          </p>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-stone-100">
          <span className="flex items-center gap-1 text-xs text-stone-400">
            <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 0a8 8 0 110 16A8 8 0 018 0zm0 2a6 6 0 100 12A6 6 0 008 2zm.5 2v4.25l2.5 1.5-.5.87L7.5 8.75V4h1z" />
            </svg>
            {formatHatchTime(tier.hatchMs)}
          </span>
          <button
            onClick={handleClick}
            disabled={!canBuy}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-sm font-semibold transition-colors ${
              canBuy
                ? 'bg-amber-500 hover:bg-amber-600 text-white'
                : 'bg-stone-100 text-stone-400 cursor-not-allowed'
            }`}
          >
            <CoinIcon size={14} inert />
            {tier.cost}
          </button>
        </div>
      </div>
    </div>
  );
}
